<template>
  <div v-if="offer"
       data-testid="customer-booking-resume"
       :class="$setClasses">
    <card-flex v-if="offer.mover"
               :class="$vClass('mover')"
               :mobile-wrapper-column="true">
      <mover-description :mover="offer.mover"
                         class="no-padding"
                         :display-description="false"
                         :display-tags="isPrebooking"
                         :display-rates="false"
                         hide-no-rating/>
    </card-flex>
    <h3 :class="$vClass('subtitle')">{{$t('resume.move')}}</h3>
    <quote v-model="activeCollapses"
           :disabled="false"
           :offer="offer"
           :collapse-items="collapseItems"
           :options-type="optionsType"
           checkbox-options
           class="--margin-top --margin-bottom"
           hide-info-mover
           is-box
           is-collapse>
      <template v-slot:collapse-items>
        <el-collapse-item v-if="!loadingDate.hidden"
                          :disabled="loadingDate.disabled"
                          name="loading-date">
          <template #title>
            <font-awesome-icon :class="['resume__icon-check',
                                  loadingDate.check ? '--is-check' : '']"
                               :icon="['fas',
                                  loadingDate.check ? 'circle-check' : 'circle-xmark']"/>
            <font-awesome-icon class="resume__icon"
                               :icon="['fal', 'calendar']"/>
            <span :class="['resume__title', 'bold']">
                  {{ $t('resume.preBooking.dateTitle') }}</span>
            <span class="resume__subtitle">{{ loadingDate.date }}</span>
          </template>
          <loading-date v-model="date"
                        :allowDateChange="stepConfig?.options?.allowDateChange"
                        :loadDateFrom="offer?.load1_date_from"
                        :loadDateTo="offer?.load1_date_to"
                        class="--margin-bottom"
                        @changeDateNotSuitable="dateNotSuitable = $event"/>
        </el-collapse-item>
      </template>
      <template v-if="beforePrice && !hidePriceInfo"
                v-slot:before-price>
        <h3 v-if="beforePrice.title"
            :class="['u-margin-top-default', $vClass('subtitle')]">
          {{ beforePrice.title }}</h3>
        <span v-if="beforePrice.content"
              class="u-font-size-default"
              v-html="beforePrice.content"></span>
      </template>
    </quote>
    <card-flex v-if="cardContent"
               :class="[$vClass('memo'), 'u-border-none',
                'step-comparator-wrapper__header__card-flex']"
               :content="cardContent"
               :icon="stepConfig.card.icon"
               :icon-green="true"
               :image="cardImage"
               :mobile-wrapper-column="true"
               no-box-shadow/>
    <div v-if="boxesContent?.length"
         :class="[$vClass('box'), 'u-font-size-default u-display-flex']">
      <div v-for="(box, index) of boxesContent"
          :key="index"
          :class="[$vClass('box__wrapper'), 'u-is-box']">
        <font-awesome-icon :class="[$vClass('box__icon'),
          box.color ? `u-color-${box.color}` : '']"
                            :icon="box.icon"></font-awesome-icon>
        <p v-html="box.content"></p>
      </div>
    </div>
    <p v-if="footerText"
       :class="`---margin-hz ${$vClass('nota')}`">
      {{ footerText }}
    </p>
    <cgu ref="cgu"
         v-if="isMinarm && isInitialStatus"
         v-model="cgu"
         :show-error="cguError"
         class="---margin-hz"></cgu>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { useConfigStore, useServiceStore, useOfferStore, useComparatorStore, useActionsStore } from '@/stores';
import CardFlex from '../../../utils/components/CardFlex.vue';
import Cgu from '../../../utils/components/Cgu.vue';
import Quote from '../../../utils/components/Quote.vue';
import LoadingDate from '../../../utils/components/LoadingDate.vue';
import MoverDescription from '../../offer/fragments/MoverDescription.vue';
import { BREAKPOINTS } from '@/types/breakpoints';
import { useMoveManagerEmits, useMoveManager, useMoveManagerProps } from '@/composables/useMoveManager';
import { computed } from 'vue';
import offersHelper from '@/utils/helpers/offers';

export default {
  name: 'StepResume',
  nameClassStyle: 'resume',
  emits: useMoveManagerEmits,
  props: useMoveManagerProps,
  setup(props, { emit }) {
    const context = useMoveManager(props, { emit });
    context.price = computed(() =>  props.offer
        ? offersHelper.getPrice(props.offer, context.demand.value.show_price_ht) : '');
    return context;
  },
  components: {
    CardFlex,
    Cgu,
    Quote,
    LoadingDate,
    MoverDescription,
  },
  data() {
    return {
      cgu: false,
      cguError: false,
      date: null,
      dateNotSuitable: false,
      activeCollapses: undefined,
    };
  },
  computed: {
    ...mapState(useConfigStore, ['comparatorConfig', 'isPrebooking']),
    collapseItems() {
      return this.stepConfig?.options?.collapseItems;
    },
    optionsType() {
      return this.stepConfig?.options?.optionsType;
    },
    stepConfig() {
      return this.comparatorConfig.resume;
    },
    loadingDate() {
      return {
        date: this.$helpers.demand.formatBetweenDates(
          this.offer?.load1_date_from,
          this.offer?.load1_date_to,
        ),
        check: true,
        ...this.collapseItems?.loadingDate,
      };
    },
    beforePrice() {
      return this.stepConfig?.options?.beforePrice ? {
        title: this.getTextVersion(this.stepConfig.options.beforePrice.title),
        content: this.getTextVersion(this.stepConfig.options.beforePrice.content,
          { nbDaysBookingLimit: this.nbDaysBookingLimit }),
      } : undefined;
    },
    boxesContent() {
      return this.stepConfig?.options?.boxes?.map((box) => ({
          ...box,
          content: this.getTextVersion(box.content),
        })) || [];
    },
    priceDiffLabel() {
      return `${this.$n(this.priceDiff, 'currency')} ${this.exclTax ? 'HT' : 'TTC'}`;
    },
    formattedDate() {
      return this.date && this.dateNotSuitable
        ? this.$helpers.format.formatSQLDate(this.date)
        : null;
    },
    cardImage() {
      return this.stepConfig
        && this.getTextVersion(this.stepConfig.card?.image);
    },
    cardContent() {
      if (this.stepConfig?.card) {
        return this.getTextVersion(this.stepConfig.card.content,
          this.getTextParams);
      }
      return '';
    },
    footerText() {
      return this.stepConfig?.footerText
        && this.getTextVersion(this.stepConfig.footerText, {
          name: this.offer.mover.name,
          nbDaysBookingLimit: this.nbDaysBookingLimit,
        });
    },
  },
  created() {
    this.activeCollapses = (this.collapseItems?.value
      && Array.isArray(this.collapseItems.value))
      ? this.collapseItems.value : [];
    if (BREAKPOINTS.tablet > window.innerWidth) {
      this.activeCollapses = [];
    }
  },
  methods: {
    ...mapActions(useConfigStore, ['displayWarningModal', 'setLoading']),
    ...mapActions(useServiceStore, ['postServices']),
    ...mapActions(useOfferStore, ['postOptions']),
    ...mapActions(useComparatorStore, ['setPrebookDate']),
    ...mapActions(useActionsStore, ['track']),
    async setOptions() {
      const offerOptions = {
        id: this.offer.id,
        options: this.offer.options.reduce((acc, option) => {
          if (option.is_chosen) {
            acc.push(option.id);
          }
          return acc;
        }, []),
      };
      await this.postOptions(offerOptions);
    },
    nextStep(condition) {
      if (condition !== false) {
        this.$router.push({
          name: 'booking',
          params: {
            id: this.$route.params.id,
          },
          hash: `#${this.comparatorConfig.resume.next.name}`,
        });
      }
    },
    async saveOptionAndPrebookDate() {
      await this.setOptions();
      this.savePrebookDate();
    },
    savePrebookDate() {
      this.setPrebookDate(this.formattedDate);
      this.nextStep();
    },
    async chooseAction() {
      const result = await this.choose();
      this.nextStep(result);
    },
    async preBookAction() {
      this.track(this.dateNotSuitable ? 'withDatePrebook' : 'prebook');
      this.setPrebookDate(this.formattedDate);
      const result = await this.preBook(null);
      this.nextStep(result);
    },
    prebookActionMinarm() {
      if (!this.cgu) {
        this.cguError = true;
        if (this.$refs?.cgu?.$el) {
          this.$refs.cgu.$el.scrollIntoView();
        }
        return;
      }
      this.prebookActionWithWarning();
    },
    prebookActionWithWarning() {
      if (this.warningVisible) {
        this.showWarning('preBookAction');
      } else {
        this.preBookAction();
      }
    },
    bookActionWithWarning() {
      if (this.warningVisible) {
        this.showWarning('bookAction');
      } else {
        this.bookAction();
      }
    },
    async bookAction() {
      const result = await this.book();
      this.nextStep(result);
    },
    showWarning(nextAction) {
      this.setLoading(true);
      const typeModalBooking = this.isPrebooking ? 'preBooking' : 'booking';
      this.displayWarningModal({
        display: true,
        content: this.isMinarm
          ? this.$t(`resume.${typeModalBooking}.warningMinarm`,
            { priceDiff: this.priceDiffLabel })
          : this.$t(`resume.${typeModalBooking}.warningCommissionnaire`,
            { priceDiff: this.priceDiffLabel }),
        action: () => this[nextAction ?? 'nextStep'](),
      });
      this.setLoading(false);
    },
    formatPrice(price, priceType) {
      return `${this.$n(price, 'currency')} ${this.$t('base.priceType', priceType)}`;
    },
  },
  watch: {
    cgu(value) {
      this.cguError = !value;
    },
  },
};
</script>

<style scoped lang="scss">
@use '@nextories/vue-core-components/src/assets/scss/components/templates/NxtCoreResume.scss' as *;

.resume {
  margin-bottom: 20px;

  .--margin-bottom {
    margin-bottom: 20px;
  }

  .---margin-hz {
    margin: {
      right: 20px;
      left: 20px;
    };
  }
  .--margin-top {
    margin-top: 20px;
  }

  .recommend-offer {
    margin-bottom:var(--nxt-space-default);
  }

  :deep(.loading-date__wrapper) {
    margin-left: 10px;
  }

  &__mover {

    &.el-card {
      border: 0;

      :deep(.el-card__body) {
        padding: 0 0 var(--nxt-space-small) 0;

        .card-flex {
          &__wrapper {
            align-items: inherit;
          }

          &__content {
            margin: 0;
            text-align: inherit;
          }
        }
      }
    }
  }

  &__memo.step-comparator-wrapper__header__card-flex {
    background: transparent !important;

    :deep(svg) {
      color: var(--nxt-color-secondary);
    }

    :deep(.card-flex__content) {
      text-align: left;
    }
  }

  &__nota {
    margin-top: 20px;
    font-size: 15px;
  }
  .loading {
    position: absolute;
    top: 0;
    left: 0;
  }

  &__box {
    gap: 10px;
    flex-direction: column;

    @include breakpoint(tablet) {
      flex-direction: row;
    }

    &__wrapper {
      flex: 1;
      display: grid;
      grid-template-columns: 80px 1fr;
      align-items: center;
    }

    &__icon {
      padding-right: var(--nxt-space-small);
      font-size: 50px;
      justify-self: center;
    }
  }

  .recommend-offer {
    border-radius: var(--nxt-border-radius-base);
  }

  :deep(.mover-description__mover__img-wrapper) {
    border: 1px solid var(--nxt-color-background-sub-menu);
  }

  :deep(.el-collapse-item.is-disabled) {

    .el-collapse-item__header {
      color: var(--nxt-color-primary);
      cursor: initial;
    }
  }

  :deep(.total.--is-box .total__discount) {
    align-items: center;
  }

  :deep(.total.--is-box .total__discount__banner) {
    align-self: center;
    width: fit-content;
    max-width: calc(100% - 24px);
    border-radius: 0 0 7px 7px;
    white-space: normal;
  }
}
:deep(.card-flex__img) {
  height: 100px;
}
:deep(.table__item__icon.fa-times-circle),
:deep(.table__item__icon.fa-circle-xmark) {
  color: var(--nxt-color-danger);
}
</style>
